<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

const props = withDefaults(defineProps<{
  scrollBy?: number
}>(), {
  scrollBy: 320,
})

const container = ref<HTMLElement | null>(null)
const canLeft = ref(false)
const canRight = ref(false)

const checkScrollLimits = () => {
  if (!container.value) return
  const { scrollLeft, scrollWidth, clientWidth } = container.value
  canLeft.value = scrollLeft > 5
  canRight.value = scrollLeft < scrollWidth - clientWidth - 5
}

// Throttle event checks to prevent scroll lag and layout thrashing
let isThrottled = false
const update = () => {
  if (isThrottled) return
  isThrottled = true
  setTimeout(() => {
    checkScrollLimits()
    isThrottled = false
  }, 100)
}

const goLeft = () => {
  if (!container.value) return
  container.value.scrollBy({ left: -props.scrollBy, behavior: 'smooth' })
  // Force a check right after the scroll action starts and after it ends
  checkScrollLimits()
  setTimeout(checkScrollLimits, 400)
}

const goRight = () => {
  if (!container.value) return
  container.value.scrollBy({ left: props.scrollBy, behavior: 'smooth' })
  // Force a check right after the scroll action starts and after it ends
  checkScrollLimits()
  setTimeout(checkScrollLimits, 400)
}

onMounted(() => {
  checkScrollLimits()
  container.value?.addEventListener('scroll', update, { passive: true })
})

onUnmounted(() => {
  container.value?.removeEventListener('scroll', update)
})
</script>

<template>
  <div class="relative w-full">
    <Transition name="arrow-fade">
      <button v-if="canLeft" class="arrow arrow-left" aria-label="Scorri a sinistra" @click="goLeft">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
    </Transition>

    <div ref="container" class="scroll-track w-full overflow-x-auto">
      <div class="flex flex-row gap-5 w-max mx-auto px-12 py-2">
        <slot />
      </div>
    </div>

    <Transition name="arrow-fade">
      <button v-if="canRight" class="arrow arrow-right" aria-label="Scorri a destra" @click="goRight">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </Transition>
  </div>
</template>

<style scoped>
.scroll-track {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.scroll-track::-webkit-scrollbar { display: none; }

.arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  color: #1f2937;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.arrow:hover {
  transform: translateY(-50%) scale(1.12);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.32);
}
.arrow svg { width: 1.2rem; height: 1.2rem; }
.arrow-left { left: 0.375rem; }
.arrow-right { right: 0.375rem; }

.arrow-fade-enter-active,
.arrow-fade-leave-active { transition: opacity 0.2s ease; }
.arrow-fade-enter-from,
.arrow-fade-leave-to { opacity: 0; }
</style>
